import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const HPOS = () => (
  <>NLP OS<sup className="text-[65%] align-super">™</sup></>
);

// ── Plans data ──────────────────────────────────────────────────────────────

const CORE_FEATURES = [
  '1 coach account',
  'Up to 10 active clients / respondents',
  'NLP OS assessment platform',
  'AI analysis on assessment results',
  'Interactive AI follow-up questions on results',
  'AI Coach Clone Chat',
  'Coach knowledge base / vector store',
  'Basic in-app CRM, notes, and client tracking',
  'Weekly group implementation calls',
];

const GROWTH_FEATURES = [
  'Everything in NLP OS Core, plus:',
  'Up to 25 active clients / respondents',
  'CoachGrowth OS',
  'AI Coach Clone lead magnet funnel',
  'Assessment lead magnet funnel',
  'Landing pages & booking calendar',
  'CRM pipeline',
  'Email / SMS follow-up automations',
  'Lead nurture & enrollment sequences',
  'Templates / snapshots',
  'Weekly execution calls',
  'Enrollment / sales training',
];

const PARTNER_FEATURES = [
  'Up to 10 coach accounts',
  'Up to 50 active clients / respondents total',
  'NLP OS platform access for all coaches',
  'AI analysis on assessment results',
  'Interactive AI follow-up questions',
  'AI Coach Clone Chat',
  'Coach knowledge base / vector store',
  'Basic in-app CRM, notes & client tracking',
];

const ACCELERATOR_ITEMS = [
  'Platform onboarding walkthrough',
  'NLP OS setup guidance',
  'Assessment setup and client invitation process',
  'AI Coach Clone Chat setup guidance',
  'Coach knowledge base setup guidance',
  'First Client Pipeline System',
  '30-day launch action plan',
  'Pricing calculator and offer setup',
  'Client onboarding scripts',
  'Warm outreach templates',
  'Assessment lead magnet campaign guidance',
  'Weekly implementation support during launch period',
];

// ── Sub-components ───────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-sage-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 text-sm">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-chocolate-600">
          {item.endsWith(':') ? (
            <span className="font-semibold text-chocolate-800">{item}</span>
          ) : (
            <>
              <CheckIcon />
              <span>{item}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

// ── Partner Application Form ─────────────────────────────────────────────────

function PartnerApplicationForm() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      const { error } = await supabase
        .from('partner_applications')
        .insert({ full_name: form.full_name.trim(), email: form.email.trim(), phone: form.phone.trim() || null });
      if (error) throw error;
      setStatus('success');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-6">
        <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="font-semibold text-chocolate-800 mb-1">Application received!</p>
        <p className="text-sm text-chocolate-500">We'll be in touch shortly to discuss the Partner Plan.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-chocolate-700 mb-1">Full name *</label>
        <input
          type="text"
          required
          value={form.full_name}
          onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
          placeholder="Your name"
          className="w-full px-3 py-2.5 text-sm border border-chocolate-200 rounded-xl bg-cream-50 text-chocolate-800 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-chocolate-700 mb-1">Email address *</label>
        <input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="you@example.com"
          className="w-full px-3 py-2.5 text-sm border border-chocolate-200 rounded-xl bg-cream-50 text-chocolate-800 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-chocolate-700 mb-1">Phone number</label>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          placeholder="+1 555 000 0000"
          className="w-full px-3 py-2.5 text-sm border border-chocolate-200 rounded-xl bg-cream-50 text-chocolate-800 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent"
        />
      </div>
      {status === 'error' && (
        <p className="text-xs text-red-600">{errorMsg}</p>
      )}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full py-3 rounded-xl bg-chocolate-700 hover:bg-chocolate-800 text-white text-sm font-semibold transition-colors disabled:opacity-60"
      >
        {status === 'submitting' ? 'Submitting…' : 'Apply for Partner Plan'}
      </button>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PricingPage() {
  const [annual, setAnnual] = useState(false);

  const plans = [
    {
      id: 'core',
      name: 'NLP OS Core',
      badge: null,
      featured: false,
      monthlyPrice: 99,
      annualPrice: 990,
      description: 'For coaches who want the NLP OS platform, assessment, AI analysis, AI Coach Clone Chat, and basic client management.',
      features: CORE_FEATURES,
      extraUsage: 'Extra active clients: $10/month each',
      breakEven: 'One client at $99/month can cover the Core platform.',
      cta: 'Start with Core',
      ctaHref: '/register',
    },
    {
      id: 'growth',
      name: 'NLP OS Growth OS',
      badge: 'Recommended',
      featured: true,
      monthlyPrice: 197,
      annualPrice: 1970,
      description: 'For coaches who want the full NLP OS platform plus a client acquisition, follow-up, and growth system.',
      features: GROWTH_FEATURES,
      extraUsage: 'Extra active clients: $10/month each',
      breakEven: 'Two clients at $99/month covers Growth OS ($198 vs $197).',
      cta: 'Start with Growth OS',
      ctaHref: '/register',
    },
  ];

  return (
    <div className="min-h-screen bg-brand-5 font-urbanist">
      {/* Minimal nav bar */}
      <header className="sticky top-0 z-30 bg-cream-50/90 backdrop-blur border-b border-chocolate-200/30">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/nlp_os_logo_off_white_creme_bg_.png"
              alt="NLP OS"
              className="h-8 w-auto"
            />
          </Link>
          <Link to="/login" className="text-xs text-chocolate-500 hover:text-chocolate-700 transition-colors">
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-24">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="text-center pt-16 pb-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 bg-sage-100 text-sage-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 uppercase tracking-wide">
              Pricing
            </div>
            <h1 className="text-heading-sm sm:text-heading-md text-chocolate-800 mb-4 max-w-3xl mx-auto">
              Choose the NLP OS Plan That Fits Your Coaching Business
            </h1>
            <p className="text-paragraph-lg text-chocolate-500 max-w-2xl mx-auto mb-8">
              Start with the <HPOS /> platform, AI insights, AI Coach Clone Chat, client tracking, and optional growth systems to help you turn insight into recurring coaching revenue.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center bg-cream-200 rounded-full p-1 gap-1">
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  !annual ? 'bg-white text-chocolate-800 shadow-soft' : 'text-chocolate-500 hover:text-chocolate-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  annual ? 'bg-white text-chocolate-800 shadow-soft' : 'text-chocolate-500 hover:text-chocolate-700'
                }`}
              >
                Annual — Save 2 Months
              </button>
            </div>

            {annual && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 inline-flex items-center gap-2 bg-sage-50 border border-sage-200 text-sage-700 text-sm px-5 py-2.5 rounded-xl"
              >
                <svg className="w-4 h-4 text-sage-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Annual plans include 12 months access for the price of 10, plus the NLP OS Launch Accelerator included free as a special launch bonus.
              </motion.div>
            )}
          </motion.div>
        </section>

        {/* ── Pricing cards ─────────────────────────────────────────────────── */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Core & Growth */}
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className={`relative flex flex-col rounded-3xl border p-7 ${
                plan.featured
                  ? 'bg-chocolate-800 border-chocolate-700 shadow-soft-lg'
                  : 'bg-white border-chocolate-100 shadow-soft'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sage-500 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                  {plan.badge}
                </span>
              )}

              <div className="mb-5">
                <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${plan.featured ? 'text-sage-300' : 'text-sage-600'}`}>
                  {plan.name}
                </p>
                <div className="flex items-end gap-1 mb-1">
                  {annual ? (
                    <>
                      <span className={`text-4xl font-bold tracking-tight ${plan.featured ? 'text-white' : 'text-chocolate-800'}`}>
                        ${plan.annualPrice.toLocaleString()}
                      </span>
                      <span className={`text-sm mb-1.5 ${plan.featured ? 'text-chocolate-300' : 'text-chocolate-400'}`}>/year</span>
                    </>
                  ) : (
                    <>
                      <span className={`text-4xl font-bold tracking-tight ${plan.featured ? 'text-white' : 'text-chocolate-800'}`}>
                        ${plan.monthlyPrice}
                      </span>
                      <span className={`text-sm mb-1.5 ${plan.featured ? 'text-chocolate-300' : 'text-chocolate-400'}`}>/month</span>
                    </>
                  )}
                </div>

                {annual ? (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 bg-sage-100 text-sage-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Launch Accelerator included free
                    </span>
                  </div>
                ) : (
                  <p className={`text-xs ${plan.featured ? 'text-chocolate-300' : 'text-chocolate-400'}`}>
                    + $997 NLP OS Launch Accelerator (one-time)
                  </p>
                )}
              </div>

              <p className={`text-sm leading-relaxed mb-6 ${plan.featured ? 'text-chocolate-200' : 'text-chocolate-500'}`}>
                {plan.description}
              </p>

              <div className={`flex-1 mb-6 ${plan.featured ? '[&_li]:text-chocolate-200 [&_svg]:text-sage-300' : ''}`}>
                <FeatureList items={plan.features} />
              </div>

              <div className={`text-xs rounded-xl px-3 py-2.5 mb-5 ${plan.featured ? 'bg-white/10 text-chocolate-200' : 'bg-brand-5 text-chocolate-500'}`}>
                <span className="font-semibold">Extra usage: </span>{plan.extraUsage}
              </div>

              <div className={`text-xs italic mb-6 ${plan.featured ? 'text-sage-300' : 'text-sage-600'}`}>
                &#9670; {plan.breakEven}
              </div>

              <Link
                to={plan.ctaHref}
                className={`text-center py-3 rounded-xl text-sm font-semibold transition-colors ${
                  plan.featured
                    ? 'bg-sage-500 hover:bg-sage-600 text-white'
                    : 'bg-chocolate-800 hover:bg-chocolate-900 text-white'
                }`}
              >
                {plan.cta}
              </Link>
            </motion.div>
          ))}

          {/* Partner plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14 }}
            className="flex flex-col rounded-3xl border border-chocolate-100 bg-white shadow-soft p-7"
          >
            <div className="mb-5">
              <p className="text-xs font-bold uppercase tracking-widest text-chocolate-500 mb-2">NLP OS Partner Plan</p>
              <div className="flex items-end gap-1 mb-1">
                {annual ? (
                  <>
                    <span className="text-4xl font-bold tracking-tight text-chocolate-800">$4,970</span>
                    <span className="text-sm mb-1.5 text-chocolate-400">/year</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold tracking-tight text-chocolate-800">$497</span>
                    <span className="text-sm mb-1.5 text-chocolate-400">/month</span>
                  </>
                )}
              </div>
              {annual ? (
                <span className="inline-flex items-center gap-1 bg-sage-100 text-sage-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Partner Launch Accelerator included free
                </span>
              ) : (
                <p className="text-xs text-chocolate-400">
                  + $997 Partner Launch Accelerator (one-time)
                </p>
              )}
            </div>

            <p className="text-sm leading-relaxed text-chocolate-500 mb-6">
              For partners, trainers, coaching schools, or organisations who want to bring multiple coaches onto <HPOS />.
            </p>

            <div className="flex-1 mb-4">
              <FeatureList items={PARTNER_FEATURES} />
            </div>

            <div className="text-xs rounded-xl px-3 py-2.5 bg-brand-5 text-chocolate-500 mb-5">
              <p><span className="font-semibold">Extra coaches:</span> $29/month each</p>
              <p className="mt-1"><span className="font-semibold">Extra clients:</span> $5/month each</p>
            </div>

            <div className="border-t border-chocolate-100 pt-5">
              <p className="text-xs font-semibold text-chocolate-700 mb-3">Apply for Partner Plan</p>
              <PartnerApplicationForm />
            </div>
          </motion.div>
        </section>

        {/* ── HPOS Launch Accelerator ───────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-chocolate-100 bg-white shadow-soft p-8 mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-1.5 bg-sage-100 text-sage-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                {annual ? 'Launch Bonus — Included Free' : '$997 Value'}
              </div>
              <h2 className="text-text-2xl text-chocolate-800 mb-1">NLP OS Launch Accelerator</h2>
              <p className="text-sm text-chocolate-500 max-w-xl">
                Helps new coaches, trainers, and partners get implemented quickly so they can start using <HPOS /> with real clients.
              </p>
            </div>
            {!annual && (
              <div className="text-right flex-shrink-0">
                <p className="text-2xl font-bold text-chocolate-800">$997</p>
                <p className="text-xs text-chocolate-400">one-time fee (monthly plans)</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ACCELERATOR_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-2.5">
                <CheckIcon />
                <span className="text-sm text-chocolate-600">{item}</span>
              </div>
            ))}
          </div>

          {annual && (
            <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-sage-700 bg-sage-50 border border-sage-200 px-4 py-3 rounded-xl">
              <svg className="w-4 h-4 text-sage-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Annual plans include this free — a $997 saving during our launch period.
            </div>
          )}
        </motion.section>

        {/* ── Suggested client pricing ──────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-heading-xs text-chocolate-800 mb-2">Suggested Pricing for Your Own Clients</h2>
            <p className="text-sm text-chocolate-500 max-w-xl mx-auto">
              These are suggested client-facing pricing models coaches can use when offering NLP OS-supported coaching.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                name: 'AI Insight Plan',
                price: '$49/month',
                items: [
                  'AI assessment insight',
                  'AI analysis follow-up',
                  'AI Coach Clone Chat',
                ],
              },
              {
                name: 'AI Coaching Support',
                price: '$99/month',
                items: [
                  'AI assessment insight',
                  'AI Coach Clone Chat',
                  'Follow-up support between sessions',
                  'Ongoing AI-supported personal development',
                ],
              },
              {
                name: 'AI Voice Plan',
                price: '$99–$149/month',
                items: [
                  'AI assessment insight',
                  'AI Coach Clone Chat',
                  'AI Coach Clone Voice',
                ],
              },
            ].map((tier) => (
              <div key={tier.name} className="rounded-2xl border border-chocolate-100 bg-white shadow-soft p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-sage-600 mb-2">{tier.name}</p>
                <p className="text-2xl font-bold text-chocolate-800 mb-4">{tier.price}</p>
                <FeatureList items={tier.items} />
              </div>
            ))}
          </div>
        </motion.section>

        {/* ── Break-even section ─────────────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-heading-xs text-chocolate-800 mb-2">How Quickly Can NLP OS Pay for Itself?</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-4">
            {[
              {
                plan: 'NLP OS Core',
                price: '$99/month',
                at99: { clients: 1, math: '1 × $99 = $99/month' },
                at49: { clients: 3, math: '3 × $49 = $147/month' },
              },
              {
                plan: 'NLP OS Growth OS',
                price: '$197/month',
                at99: { clients: 2, math: '2 × $99 = $198/month' },
                at49: { clients: 5, math: '5 × $49 = $245/month' },
              },
            ].map((row) => (
              <div key={row.plan} className="rounded-2xl border border-chocolate-100 bg-white shadow-soft p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-chocolate-400 mb-1">{row.plan}</p>
                <p className="text-xl font-bold text-chocolate-800 mb-4">{row.price}</p>
                <div className="space-y-3">
                  <div className="bg-brand-5 rounded-xl p-3">
                    <p className="text-xs font-semibold text-chocolate-600 mb-0.5">At $99/client/month</p>
                    <p className="text-sm font-bold text-sage-600">{row.at99.clients} client{row.at99.clients > 1 ? 's' : ''} = break-even</p>
                    <p className="text-xs text-chocolate-400 mt-0.5">{row.at99.math}</p>
                  </div>
                  <div className="bg-brand-5 rounded-xl p-3">
                    <p className="text-xs font-semibold text-chocolate-600 mb-0.5">At $49/client/month</p>
                    <p className="text-sm font-bold text-sage-600">{row.at49.clients} clients = break-even</p>
                    <p className="text-xs text-chocolate-400 mt-0.5">{row.at49.math}</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="rounded-2xl border border-sage-200 bg-sage-50 p-6 flex flex-col justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-sage-600 mb-3">The bottom line</p>
                <p className="text-sm text-sage-800 leading-relaxed font-medium">
                  Get 1–5 paying clients and the platform can pay for itself, depending on your client pricing.
                </p>
              </div>
              <Link
                to="/revenuecalculator"
                className="mt-5 inline-flex items-center justify-center gap-2 bg-sage-600 hover:bg-sage-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
              >
                Calculate My Revenue
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>

          <p className="text-center text-xs text-chocolate-400 max-w-2xl mx-auto">
            Revenue examples are for illustration only and are not guarantees of income. Actual results depend on your offer, audience, pricing, follow-up, sales activity, and client delivery.
          </p>
        </motion.section>

        {/* ── Profit Dashboard coming soon ─────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-chocolate-100 bg-white shadow-soft p-8 mb-12"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
            <div className="w-10 h-10 rounded-2xl bg-sage-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-text-xl text-chocolate-800">Profit Dashboard — Included</h2>
              <p className="text-sm text-chocolate-400">Help coaches and partners build a sustainable, growing coaching business.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              'Revenue calculator',
              'Break-even calculator',
              'Revenue goal tracker',
              'AI Profit Coach',
              'Offer builder',
              '30-day action plan generator',
              'Campaign suggestions',
              'Partner revenue calculator',
            ].map((feature) => (
              <div key={feature} className="flex items-start gap-2">
                <CheckIcon />
                <span className="text-sm text-chocolate-600">{feature}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* ── Final CTA ─────────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-chocolate-400 text-xs mt-8">
            &copy; {new Date().getFullYear()} NLP OS<sup className="text-[65%] align-super">™</sup> — All rights reserved
          </p>
        </motion.div>
      </main>
    </div>
  );
}
